import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GameController, Coins, ArrowRight, Trophy, Scales } from "@phosphor-icons/react";

/**
 * Games — public portal. Anyone can browse; entering a listed game deducts
 * that game's admin-configured entry cost in Credits. The seeded first game
 * is "The Dwamena & Associates Global Legal Championship Contest" — a click
 * on the sidebar Games button lands here so that game is featured on top.
 */
export default function GamesPage() {
  const { user, refresh } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/games");
      setGames(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load games");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enter = async (g) => {
    if (!user) {
      toast.error("Sign in to enter a game");
      return;
    }
    const cost = g.entry_cost_credits || 0;
    if (cost > 0 && !window.confirm(
      `Enter "${g.name}"? This will deduct ${cost} Credit${cost === 1 ? "" : "s"} from your balance.`
    )) return;
    setEntering(g.game_id);
    try {
      const r = await api.post(`/games/${g.game_id}/enter`);
      if (r.data.already_entered) {
        toast.success("You're already in today's session — jumping you in.");
      } else if (r.data.cost > 0) {
        toast.success(`Entered! -${r.data.cost} Credits`);
      } else {
        toast.success("Entered!");
      }
      refresh?.();
      // Legal Championship has a dedicated engine; other games land on the portal.
      if (g.game_id === "game_legal_championship" || g.slug === "legal-championship") {
        navigate(`/games/legal-championship`);
      } else {
        navigate(`/games/${g.game_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enter");
    } finally { setEntering(null); }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="games-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black flex items-center gap-3">
            <GameController size={32} weight="fill" className="text-emerald-400" />
            Games
          </h1>
          <p className="text-white/60 mt-1">
            Browse the Tain Games portal. Entry to each game costs Credits — set by admin per title.
          </p>
        </div>
        {user && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm">
            <Coins size={16} weight="fill" /> {user.points ?? 0} Credits
          </div>
        )}
      </header>

      {loading && <p className="text-white/50 text-sm">Loading games…</p>}

      {!loading && games.length === 0 && (
        <p className="text-white/60 text-sm">No games available yet.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="games-grid">
        {games.map((g, i) => (
          <Card
            key={g.game_id}
            data-testid={`game-card-${g.game_id}`}
            className="bg-[#121212] border-white/10 overflow-hidden"
          >
            <div
              className={`h-32 bg-gradient-to-br ${g.cover_gradient || "from-blue-500 to-indigo-600"} flex items-end p-4 relative`}
            >
              {g.featured && (
                <Badge className="absolute top-3 right-3 bg-yellow-400/95 text-black border-0 gap-1">
                  <Trophy size={12} weight="fill" /> Featured
                </Badge>
              )}
              <Scales size={40} weight="fill" className="text-white/25 absolute -bottom-2 -right-2" />
              <h2 className="font-heading text-white text-xl font-black drop-shadow-lg leading-tight relative">
                {g.name}
              </h2>
            </div>
            <CardContent className="p-5 space-y-4">
              <p className="text-white/70 text-sm leading-relaxed">{g.description}</p>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-yellow-500/15 text-yellow-300 border-yellow-500/40 gap-1">
                    <Coins size={12} weight="fill" />
                    {g.entry_cost_credits > 0
                      ? `${g.entry_cost_credits} Credits entry`
                      : "Free to enter"}
                  </Badge>
                  {g.credit_rewards?.senior_advocate_perfect ? (
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 gap-1">
                      <Trophy size={12} weight="fill" /> Up to {g.credit_rewards.senior_advocate_perfect} cr / round
                    </Badge>
                  ) : g.credit_rewards?.round_5_champion ? (
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 gap-1">
                      <Trophy size={12} weight="fill" /> Up to {g.credit_rewards.round_5_champion} cr
                    </Badge>
                  ) : null}
                </div>
                <Button
                  data-testid={`game-enter-${g.game_id}`}
                  onClick={() => enter(g)}
                  disabled={entering === g.game_id}
                  className="min-h-[44px] bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-heading font-bold gap-2"
                >
                  {entering === g.game_id ? "Entering…" : (i === 0 ? "Play Featured" : "Enter")}
                  <ArrowRight size={16} weight="bold" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
